<template>
  <div>
    <!-- #region NAVBAR   -->
    <nav >
      <ul class="nav-wrapper">
        <li class="icon">
          <RouterLink to="/">
            <span class="tooltip">Home</span>
            <span><i class="fa-solid fa-house text-white ml-3"> </i></span
          ></RouterLink>
        </li>

        <li>
          <a href="#">
            <div class="mx-3 text-base lg:text-2xl text-white">Reports</div>
          </a>
        </li>

        <li @click="showOptionsDialog = true" class="icon">
          <a href="#">
            <span class="tooltip">Options</span>
            <span><i class="fa-solid fa-sliders text-white ml-2"></i></span>
          </a>
        </li>

        <li
          @click="handleExportToCsv"
          class="icon"
          v-if="user.securityGroup.keys.includes(1002)"
        >
          <a href="#">
            <span class="tooltip">Export to CSV</span>
            <span
              ><i class="fa-solid fa-file-excel text-green-300 ml-2"></i
            ></span>
          </a>
        </li>

        <li @click="getData" class="icon">
          <a href="#">
            <span class="tooltip">Refresh</span>
            <span><i class="fa-solid fa-rotate ml-2"></i></span>
          </a>
        </li>

        <li>
          <a href="#">
            <div class="txt-sm text-white mr-5 cursor-default">
              {{ `Assignments: ${formatNumber(assignments.length)}` }}
            </div>
          </a>
        </li>
      </ul>
    </nav>

    <r-snackbar
      v-if="snackbarVisible"
      :backgroundColor="snackbarColor"
      :message="snackbarText"
      :timeout="3000"
      @close="snackbarVisible = false"
    >
    </r-snackbar>

    <!-- LOADING -->

    <r-spinner v-if="loading"> </r-spinner>

    <!-- OPTIONS DIALOG -->
    <r-modal v-if="showOptionsDialog" @close="showOptionsDialog = false">
      <div v-if="showOptionsDialog" class="p-5 bg-yellow-100">
        <div class="flex justify-between mb-2">
          <div class="text-xl lg:text-2xl font-bold">Report Options</div>
          <button @click="showOptionsDialog = false" class="btn-icon">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="flex gap-3">
          <div>
            <label
              for="report-type"
              class="block text-sm font-medium leading-6 text-gray-900"
              >Report Type</label
            >
            <select
              class="block w-64 py-2 rounded-md border-0 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6"
              v-model="reportType"
              required
            >
              <option
                v-for="type in reportTypeOptions"
                :key="type"
                :value="type"
              >
                {{ type }}
              </option>
            </select>
          </div>

          <div>
            <label
              for="start-date"
              class="block text-sm font-medium leading-6 text-gray-900"
              >Start Date</label
            >

            <input
              class="block w-32 rounded-md border-0 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6"
              v-model="startDate"
              type="date"
              id="start-date"
            />
          </div>

          <div>
            <label
              for="end-date"
              class="block text-sm font-medium leading-6 text-gray-900"
              >End Date</label
            >

            <input
              class="block w-32 rounded-md border-0 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6"
              v-model="endDate"
              type="date"
              id="end-date"
            />
          </div>
        </div>

        <!-- Companies -->
        <div class="border border-gray-500 rounded-md p-3 my-1">
          <div class="flex justify-between border-b border-gray-500 mb-2">
            <div class="text-xl text-blue-800 min-w-[80px]">Companies</div>
            <div class="flex mt-2">
              <div
                v-if="selectedCompanies.length"
                @click="selectedCompanies = []"
                class="my-1 ml-1 cursor-pointer text-base underline text-red-500"
              >
                Clear Selected
              </div>

              <div
                v-if="selectedCompanies.length !== companyOptions.length"
                @click="selectedCompanies = [...companyOptions]"
                class="my-1 ml-3 cursor-pointer text-base underline text-blue-600"
              >
                Select All
              </div>
            </div>
          </div>

          <div class="max-h-[200px] lg:max-h[300px] overflow-x-auto mt-3">
            <div class="relative shadow-md">
              <table
                class="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400"
              >
                <tbody>
                  <tr
                    v-for="(company, index) in companyOptions"
                    :key="index"
                    class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <td class="w-4 p-4">
                      <div class="flex items-center">
                        <input
                          v-model="selectedCompanies"
                          :id="company"
                          :value="company"
                          type="checkbox"
                          class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        />
                      </div>
                    </td>
                    <td
                      scope="row"
                      class="px-2 py-1 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                    >
                      {{ company }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Job Titles -->
        <div class="border border-gray-500 rounded-md p-3 mb-1">
          <div class="flex justify-between border-b border-gray-500 mb-2">
            <div class="text-xl text-blue-800 min-w-[80px]">Job Titles</div>
            <div class="flex mt-2">
              <div
                v-if="selectedJobTitles.length"
                @click="selectedJobTitles = []"
                class="my-1 ml-1 cursor-pointer text-base underline text-red-500"
              >
                Clear Selected
              </div>

              <div
                v-if="selectedJobTitles.length !== jobTitleOptions.length"
                @click="selectedJobTitles = [...jobTitleOptions]"
                class="my-1 ml-3 cursor-pointer text-base underline text-blue-600"
              >
                Select All
              </div>
            </div>
          </div>

          <div class="max-h-[200px] lg:max-h[300px] overflow-x-auto mt-3">
            <div class="relative shadow-md">
              <table
                class="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400"
              >
                <tbody>
                  <tr
                    v-for="(jobTitle, index) in jobTitleOptions"
                    :key="index"
                    class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <td class="w-4 p-4">
                      <div class="flex items-center">
                        <input
                          v-model="selectedJobTitles"
                          :id="jobTitle"
                          :value="jobTitle"
                          type="checkbox"
                          class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        />
                      </div>
                    </td>
                    <td
                      scope="row"
                      class="px-2 py-1 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                    >
                      {{ jobTitle }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="flex justify-end mt-2">
          <button @click="handleSaveOptions" class="btn bg-blue-500 text-white">
            Get Report
          </button>
          <button
            @click="showOptionsDialog = false"
            class="ml-1 btn bg-gray-500 text-white"
          >
            Cancel
          </button>
        </div>
      </div>
    </r-modal>

    <div v-else>
      <div class="pl-5 py-3 text-2xl bg-slate-700 text-white">
        {{
          `${reportType} - ${formatDate(startDate)} | ${formatDate(endDate)}`
        }}
      </div>
      <div v-if="assignments.length">
        <!-- Ground Assignments -->
        <div
          v-if="reportType === 'Ground Assignments'"
          class="relative overflow-x-auto"
        >
          <table
            class="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400"
          >
            <thead
              class="text-xs text-gray-200 uppercase bg-gray-500 dark:bg-gray-700 dark:text-gray-400"
            >
              <tr>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('employeeName')"
                >
                  Employee
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('employeeCompany')"
                >
                  Company
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('employeeJobTitle')"
                >
                  Job Title
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('assignmentType')"
                >
                  Assignment Type
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('assignmentRole')"
                >
                  Assignment Role
                </th>

                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('origin')"
                >
                  FROM
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('destination')"
                >
                  TO
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('start')"
                >
                  Start
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('end')"
                >
                  End
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('assygnedBy')"
                >
                  Assigned By
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="assignment in assignments"
                :key="assignment._id"
                class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <td
                  scope="row"
                  class="px-6 py-1 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                >
                  {{ assignment.employeeName }}
                </td>
                <td class="px-6 py-2">{{ assignment.employeeCompany }}</td>
                <td class="px-6 py-2">{{ assignment.employeeJobTitle }}</td>
                <td class="px-6 py-2">
                  {{ assignment.assignmentType }}
                </td>
                <td class="px-6 py-2">
                  {{
                    assignment.assignmentRole
                      ? assignment.assignmentRole
                      : "N/A"
                  }}
                </td>
                <td class="px-6 py-2">{{ assignment.origin }}</td>
                <td class="px-6 py-2">{{ assignment.destination }}</td>
                <td class="px-6 py-2">
                  {{ formatDateTime(assignment.start) }}
                </td>
                <td class="px-6 py-2">{{ formatDateTime(assignment.end) }}</td>
                <td class="px-6 py-2">{{ assignment.assignedBy }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Flight Assignments -->
        <div v-else class="relative overflow-x-auto">
          <table
            class="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400"
          >
            <thead
              class="text-xs text-gray-200 uppercase bg-gray-500 dark:bg-gray-700 dark:text-gray-400"
            >
              <tr>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('employeeName')"
                >
                  Employee
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('employeeCompany')"
                >
                  Company
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('employeeJobTitle')"
                >
                  Job Title
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('aircraftRegistration')"
                >
                  Assignment Type
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('aircraftRegistration')"
                >
                  Aircraft
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('flightNumber')"
                >
                  Flight Number
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('role')"
                >
                  Assignment Role
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('origin')"
                >
                  FROM
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('destination')"
                >
                  TO
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('start')"
                >
                  Start
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('end')"
                >
                  End
                </th>
                <th
                  scope="col"
                  class="px-6 py-2 cursor-pointer"
                  @click="sortArrayByKey('assygnedBy')"
                >
                  Assigned By
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="assignment in assignments"
                :key="assignment._id"
                class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <td
                  scope="row"
                  class="px-6 py-1 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                >
                  {{ assignment.employeeName }}
                </td>
                <td class="px-6 py-2">{{ assignment.employeeCompany }}</td>
                <td class="px-6 py-2">{{ assignment.employeeJobTitle }}</td>
                <td class="px-6 py-2">Flight</td>
                <td class="px-6 py-2">{{ assignment.aircraftRegistration }}</td>
                <td class="px-6 py-2">{{ assignment.flightNumber }}</td>
                <td class="px-6 py-2">
                  {{ assignment.role }}
                </td>
                <td class="px-6 py-2">{{ assignment.origin }}</td>
                <td class="px-6 py-2">{{ assignment.destination }}</td>
                <td class="px-6 py-2">
                  {{ formatDateTime(assignment.start) }}
                </td>
                <td class="px-6 py-2">{{ formatDateTime(assignment.end) }}</td>
                <td class="px-6 py-2">{{ assignment.assignedBy }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-else class="bottom-frame text-base">
        <div class="p-3 text-xl text-red-500">
          No assignments found with the selected filters.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../services/api";
import authentication from "../services/authentication";
import { mixin } from "../mixins/mixin";
import exportFromJSON from "export-from-json";
import RSnackbar from "../components/RSnackbar.vue";
import RSpinner from "../components/RSpinner.vue";
import RModal from "../components/RModal.vue";
import {
  Listbox,
  ListboxButton,
  ListboxLabel,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/vue";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/vue/20/solid";

export default {
  name: "administration",
  mixins: [mixin],
  components: {
    RSnackbar,
    RSpinner,
    RModal,
    Listbox,
    ListboxButton,
    ListboxLabel,
    ListboxOption,
    ListboxOptions,
    CheckIcon,
    ChevronUpDownIcon,
  },

  data() {
    return {
      loading: false,

      inactivityTimer: null,

      //-------------------------Snackbar
      snackbarVisible: false,
      snackbarText: "",
      snackbarColor: "white",
      snackbarTimeout: 5000,

      showOptionsDialog: false,

      assignments: [],

      reportType: "Flight Assignments",
      reportTypeOptions: ["Ground Assignments", "Flight Assignments"],
      startDate: "",
      endDate: "",
      //---------------------------Companies
      selectedCompanies: [],
      //---------------------------Job Titles
      selectedJobTitles: [],
    };
  },

  created() {
    // const currentMonth = new Date().getMonth() + 1;
    // const currentYear = new Date().getFullYear();
    // const startDate = new Date(currentYear, currentMonth - 1, 1);
    // const endDate = new Date(currentYear, currentMonth, 0);
    // this.startDate = startDate.toISOString().split("T")[0];
    // this.endDate = endDate.toISOString().split("T")[0];

    const now = new Date();

    // First day of the previous month UTC
    const firstDay = new Date(
      Date.UTC(now.getFullYear(), now.getMonth() - 1, 1)
    );
    this.startDate = firstDay.toISOString().split("T")[0];

    // Last day of the previous month UTC
    const lastDay = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 0));
    this.endDate = lastDay.toISOString().split("T")[0];

    if (this.user.securityGroup.companies.length) {
      this.selectedCompanies = [this.user.securityGroup.companies[0]];
    } else {
      this.selectedCompanies = [];
    }
    if (this.user.securityGroup.jobTitles.length) {
      this.selectedJobTitles = [...this.user.securityGroup.jobTitles];
    } else {
      this.selectedJobTitles = [];
    }

    this.getData();
  },

  mounted() {
    this.startInactivityTimer();
    document.addEventListener("click", this.resetInactivityTimer);
  },
  unmounted() {
    this.clearInactivityTimer();
    document.removeEventListener("click", this.resetInactivityTimer);
  },

  computed: {
    auth() {
      return this.$store.state.auth;
    },

    user() {
      return this.$store.state.user;
    },

    companyOptions() {
      if (this.auth) {
        if (this.user.securityGroup.companies.length) {
          return [...this.user.securityGroup.companies];
        } else {
          return [];
        }
      } else {
        return [];
      }
    },

    jobTitleOptions() {
      if (this.auth) {
        if (this.user.securityGroup.jobTitles.length) {
          return [...this.user.securityGroup.jobTitles];
        } else {
          return [];
        }
      } else {
        return [];
      }
    },
  },

  methods: {
    startInactivityTimer() {
      this.clearInactivityTimer();
      this.inactivityTimer = setTimeout(() => {
        this.handleAutoLogout();
      }, this.timeoutDuration);
    },

    clearInactivityTimer() {
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
      }
    },

    resetInactivityTimer() {
      this.startInactivityTimer();
    },

    async handleAutoLogout() {
      try {
        //---------------------------------------Refresh Token
        this.loading = true;
        const accessToken = await authentication.getAccessToken();
        if (!accessToken) {
          this.loading = false;
          return;
        }

        await api.get("/auth/logout", {
          headers: {
            Authorization: "Bearer " + accessToken,
          },
        });

        this.loading = false;

        this.$store.commit("updateAuth", null);
        this.$store.commit("updateUser", null);
        clearTimeout(this.inactivityTimer);
        this.$router.push({ name: "home" });
      } catch (error) {
        this.loading = false;
        this.snackbarColor = "red";
        this.snackbarVisible = true;
        this.snackbarText = `Error during auto-logout: ${error}`;

        setTimeout(() => {
          this.$store.commit("updateAuth", null);
          this.$store.commit("updateUser", null);
          clearTimeout(this.inactivityTimer);
        }, 3000);
      }
    },

    handleSaveOptions() {
      const duration =
        new Date(this.endDate).getTime() - new Date(this.startDate).getTime();

      if (duration < 0 || duration > 61 * 24 * 60 * 60 * 1000) {
        this.snackbarColor = "red";
        this.snackbarText =
          "Please check the dates selected. The range must be between 1 and 61 days.";
        this.snackbarVisible = true;
        return;
      }

      if (this.selectedCompanies.length === 0) {
        this.snackbarColor = "red";
        this.snackbarText = "Please select at least one company.";
        this.snackbarVisible = true;
        return;
      }
      if (this.selectedJobTitles.length === 0) {
        this.snackbarColor = "red";
        this.snackbarText = "Please select at least one job title.";
        this.snackbarVisible = true;
        return;
      }

      this.showOptionsDialog = false;
      this.getData();
    },

    async getData() {
      if (this.selectedCompanies.length === 0) {
        this.snackbarColor = "red";
        this.snackbarText = "Please select at least one company.";
        this.snackbarVisible = true;
        return;
      }

      if (this.selectedJobTitles.length === 0) {
        this.snackbarColor = "red";
        this.snackbarText = "Please select at least one job title.";
        this.snackbarVisible = true;
        return;
      }

      //---------------------------------------Refresh Token
      this.loading = true;
      const accessToken = await authentication.getAccessToken();
      if (!accessToken) {
        this.loading = false;
        this.$router.push({ name: "home" });
        return;
      }

      let _reportType = this.reportType;

      if (this.reportType === "Ground Assignments") {
        _reportType = "ground-assignments";
      } else if (this.reportType === "Flight Assignments") {
        _reportType = "flight-assignments";
      }

      try {
        const res = await api.get(`/reports/${_reportType}`, {
          params: {
            companyNames: this.selectedCompanies,
            jobTitles: this.selectedJobTitles,
            startDate: this.startDate,
            endDate: this.endDate,
          },
          headers: {
            Authorization: "Bearer " + accessToken,
          },
        });

        this.loading = false;

        if (res.status === 200) {
          this.assignments = res.data.assignments;
        } else {
          this.snackbarColor = "red";
          this.snackbarText = res.message;
          this.snackbarVisible = true;
        }
      } catch (error) {
        this.loading = false;
        this.snackbarColor = "red";
        this.snackbarText = error.message;
        this.snackbarVisible = true;
      }
    },

    async handleExportToCsv() {
      if (!this.user.securityGroup.keys.includes(1002)) {
        this.snackbarColor = "red";
        this.snackbarText = "You do not have permission to export reports.";
        this.snackbarVisible = true;
        return;
      }

      if (this.loading) {
        return;
      }

      this.loading = true;

      try {
        let fileName;
        const startDate = new Date(this.startDate);
        const utcYear = startDate.getUTCFullYear();
        const utcMonth = startDate.getUTCMonth(); // 0 = Jan, 11 = Dec

        // Create a "safe" date in the middle of the month in UTC
        const monthUTCDate = new Date(Date.UTC(utcYear, utcMonth, 15));

        // Get month name and year in UTC
        const monthName = monthUTCDate.toLocaleString("en-US", {
          month: "long",
          timeZone: "UTC",
        });
        const monthYear = monthUTCDate.getUTCFullYear();

        if (this.reportType === "Ground Assignments") {
          fileName = `ground_assignments_report_${monthName}_${monthYear}`;
        } else if (this.reportType === "Flight Assignments") {
          fileName = `flight_assignments_report_${monthName}_${monthYear}`;
        }

        const exportType = exportFromJSON.types.csv;
        const data = [];
        let row;

        if (this.reportType === "Ground Assignments") {
          this.assignments.forEach((assignment) => {
            row = {
              origin: assignment.origin,
              destination: assignment.destination,
              start: this.formatDateTime(assignment.start),
              end: this.formatDateTime(assignment.end),
              assignmentType: assignment.assignmentType,
              employeeId: assignment.employeeId,
              employeeName: assignment.employeeName,
              employeeJobTitle: assignment.employeeJobTitle,
              employeeCompany: assignment.employeeCompany,
              assignmentRole: assignment.assignmentRole
                ? assignment.assignmentRole
                : "N/A",
              assignedBy: assignment.assignedBy,
              remarks: assignment.remarks,
            };

            data.push(row);
          });
        } else if (this.reportType === "Flight Assignments") {
          this.assignments.forEach((assignment) => {
            row = {
              flightId: assignment.flightId,
              aircraftRegistration: assignment.aircraftRegistration,
              flightNumber: assignment.flightNumber,
              role: assignment.role,
              assignedBy: assignment.assignedBy,
              employeeId: assignment.employeeId,
              employeeName: assignment.employeeName,
              employeeJobTitle: assignment.employeeJobTitle,
              employeeCompany: assignment.employeeCompany,
              origin: assignment.origin,
              destination: assignment.destination,
              start: this.formatDateTime(assignment.start),
              end: this.formatDateTime(assignment.end),
              remarks: assignment.remarks,
            };

            data.push(row);
          });
        }

        exportFromJSON({ data, fileName, exportType });

        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.snackbarColor = "red";
        this.snackbarText = error;
        this.snackbarVisible = true;
      }
    },

    isSortedAsc(arr, key) {
      for (let i = 0; i < arr.length - 1; i++) {
        if (arr[i][key] > arr[i + 1][key]) {
          return false;
        }
      }
      return true;
    },

    sortArrayByKey(key) {
      const sortedAsc = this.isSortedAsc(this.assignments, key);

      if (sortedAsc) {
        this.assignments.sort((a, b) => (a[key] < b[key] ? 1 : -1));
      } else {
        this.assignments.sort((a, b) => (a[key] > b[key] ? 1 : -1));
      }
    },
  },
};
</script>

<style scoped></style>
